import { useQuery } from "@tanstack/react-query";
import { QueryAgentTargetsDTO } from "../dtos/index.v2";
import { getTargets } from "../api/index.v2";
import { useMemo } from "react";

export const useAgentsTargets = ({
  query,
  enabled = true,
}: {
  query: Omit<QueryAgentTargetsDTO, "organizationUId">;
  enabled?: boolean;
}) => {
  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["agentsTargets", query],
    queryFn: async ({ signal }) => getTargets({ query, signal }),
    enabled,
  });

  const sortedData = useMemo(() => {
    if (!data?.data) return undefined;

    // Sort the top-level array (user + direct reports)
    const sortedTopLevel = [...data.data].sort((a, b) => {
      const getEarliestFrom = (item: any) => {
        if (
          !item.targets ||
          !Array.isArray(item.targets) ||
          item.targets.length === 0
        )
          return Infinity;
        return Math.min(
          ...item.targets.map((t: any) => new Date(t.from).getTime())
        );
      };
      return getEarliestFrom(a) - getEarliestFrom(b);
    });

    // Sort each item's targets
    const sortedDataArray = sortedTopLevel.map((item) => {
      const newItem = { ...item };

      if (
        newItem.targets &&
        Array.isArray(newItem.targets) &&
        newItem.targets.length > 0
      ) {
        newItem.targets = [...newItem.targets].sort((a, b) => {
          const fromDateA = new Date(a.from).getTime();
          const fromDateB = new Date(b.from).getTime();

          if (fromDateA !== fromDateB) {
            return fromDateA - fromDateB;
          }

          const toDateA = new Date(a.to).getTime();
          const toDateB = new Date(b.to).getTime();
          return toDateA - toDateB;
        });
      }

      return newItem;
    });

    // Return the same structure as the original response
    return {
      ...data,
      data: sortedDataArray,
    };
  }, [data]);

  return { data: sortedData, isLoading: isFetching && isLoading };
};
